import { TGetAvailablePrizesForRedemptionResponse } from '@daymade/api-contracts';
import { PRIZE_WIN_VALIDITY_IN_DAYS } from '@daymade/common-lib/constants';
import { PrizeWinStatus } from '@daymade/common-lib/dbEnums';
import BackButton from 'components/BackButton';
import GreenPageSection from 'components/GreenPageSection';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PageTitle from 'components/PageTitle';
import PopupWithDimmedBackground from 'components/PopupWithDimmedBackground';
import PrizeCardWithTitleBelow from 'components/prizes/PrizeCardWithTitleBelow';
import RoundedButton from 'components/RoundedButton';
import Subheading from 'components/Subheading';
import { useAuthRedirectEffect } from 'hooks/account/useAuthRedirect';
import {
  unclaimedPrizeWinStatuses,
  useTreatWins,
} from 'hooks/account/useTreatWins';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { tryApi } from 'utils/api/common';
import { handleNonOkResponse } from 'utils/api/response';
import { winnerApiClient } from 'utils/apiClient';
import { extendClassName, singularOrPlural } from 'utils/common';
import { ASSET_PREFIX, colors } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

const ChooseTreats = observer(() => {
  useAuthRedirectEffect(
    window.location.pathname + window.location.search + window.location.hash,
  );

  const { currentUser, updateCurrentUser } = useUserStore();
  const uiStore = useUIStore();
  const router = useRouter();

  const {
    allPwDetails,
    setAllPwDetails,
    idxOfSelectedPw,
    setIdxOfSelectedPw,
    detailsForSelectedPw,
    prizeOptionsForSelectedPw,
    selectedPwHasBeenChosen,
    finishedChoosingPrizes,
    setFinishedChoosingPrizes,
  } = useTreatWins();

  const [selectedPrizeOption, setSelectedPrizeOption] = useState<
    | TGetAvailablePrizesForRedemptionResponse['allAvailablePrizes'][number]
    | null
  >(null); // Which prize option has the user clicked on

  const [
    waitingForSubmitPrizeChoiceResult,
    setWaitingForSubmitPrizeChoiceResult,
  ] = useState(false);

  const [
    showRedemptionSuccessMessageInPopup,
    setShowRedemptionSuccessMessageInPopup,
  ] = useState(false);

  const closePrizeOptionPopup = () => {
    if (showRedemptionSuccessMessageInPopup) {
      moveToNextPrize();
    }
    setSelectedPrizeOption(null);
    setShowRedemptionSuccessMessageInPopup(false);
  };

  const describeNumberOfMatchesInPlainEnglish = (matches: string) => {
    const [mainMatches, bonusMatches] = matches
      .split('+')
      .map((numStr) => parseInt(numStr, 10));
    return [
      mainMatches
        ? `${mainMatches} main ${singularOrPlural(
            mainMatches,
            'number',
            'numbers',
          )}`
        : null,
      bonusMatches ? 'the bonus number' : null,
    ]
      .filter((descriptionPart) => !!descriptionPart)
      .join(' + ');
  };

  const findIndexOfNextPendingPrizeWin = () =>
    allPwDetails.findIndex(
      (pw, idx) =>
        unclaimedPrizeWinStatuses.includes(pw.status) &&
        idx !== idxOfSelectedPw,
    );

  const chooseSelectedPrizeInstance = async () => {
    if (
      detailsForSelectedPw === null ||
      selectedPrizeOption === null ||
      idxOfSelectedPw === null
    ) {
      return;
    }

    setWaitingForSubmitPrizeChoiceResult(true);

    try {
      const resp = await tryApi(
        winnerApiClient.auth.submitPrizeChoice({
          body: {
            prizeWinId: detailsForSelectedPw.id,
            prizeId: selectedPrizeOption.id,
            additionalInfoFromPlayer: {},
          },
        }),
      );

      if (resp.status === HttpStatusCode.OK) {
        await updateCurrentUser();

        // Update the status of this prize win so it's no longer pending
        const updatedAllPwDetails = [...allPwDetails];
        updatedAllPwDetails[idxOfSelectedPw] = {
          ...detailsForSelectedPw,
          status: PrizeWinStatus.CHOSEN,
          chosenPrizeId: selectedPrizeOption.id,
        };
        setAllPwDetails(updatedAllPwDetails);

        // Switch to the popup's confirmation view
        setShowRedemptionSuccessMessageInPopup(true);
      } else {
        handleNonOkResponse(resp, uiStore);
      }
    } finally {
      setWaitingForSubmitPrizeChoiceResult(false);
    }
  };

  const moveToNextPrize = () => {
    const idxOfNextPW = findIndexOfNextPendingPrizeWin();
    if (idxOfNextPW < 0) {
      // No more PENDING prize wins
      setFinishedChoosingPrizes(true);
      setIdxOfSelectedPw(null);
    } else {
      setIdxOfSelectedPw(idxOfNextPW);
    }
  };

  if (!currentUser) return null;

  return (
    <MainLayout>
      <NextSeo
        title="Choose Your Treat | Win Epic Prizes | DAYMADE"
        openGraph={{
          title: 'Choose Your Treat | Win Epic Prizes | DAYMADE',
        }}
      />

      {selectedPrizeOption && (
        <PopupWithDimmedBackground
          onClose={closePrizeOptionPopup}
          bannerImage={selectedPrizeOption.heroImageLocation}
          icon={undefined}
          additionalClassName={undefined}
        >
          <h4>
            {showRedemptionSuccessMessageInPopup
              ? 'Success!'
              : selectedPrizeOption.title}
          </h4>

          {showRedemptionSuccessMessageInPopup ? (
            <p>
              Your selected treat ({selectedPrizeOption.title}) has been added
              to your account.
            </p>
          ) : (
            <div
              className="overviewBody flexCentered fullWidth text-centered"
              dangerouslySetInnerHTML={{
                __html: selectedPrizeOption.subheading,
              }}
            />
          )}

          {!showRedemptionSuccessMessageInPopup && !selectedPwHasBeenChosen && (
            <RoundedButton
              onClick={chooseSelectedPrizeInstance}
              disabled={waitingForSubmitPrizeChoiceResult}
              style={{ margin: '2em 0 0' }}
            >
              Redeem treat
            </RoundedButton>
          )}

          <div onClick={closePrizeOptionPopup} style={{ margin: '2em 0 0' }}>
            <p
              className="clickable underline bold"
              style={{ color: `${colors.primaryBrandGray}90` }}
            >
              Close
            </p>
          </div>
        </PopupWithDimmedBackground>
      )}

      <PageTitle>
        Choose a <strong>treat</strong>
      </PageTitle>

      <PaddedMaxWidthContainer largerVerticalMargin textCentered>
        {allPwDetails.length > 0 && (
          <div className="vertical-margin-1em">
            <img
              className="choose-treats-icon"
              src={`${ASSET_PREFIX}/images/my-wins/choose-treats-icon.png`}
              alt=""
            />
            <p className="xlarge xbold">
              You won{' '}
              {singularOrPlural(
                allPwDetails.length,
                'a',
                allPwDetails.length.toString(),
              )}{' '}
              {singularOrPlural(allPwDetails.length, 'treat', 'treats')} this
              week, {currentUser.firstName}!
            </p>
          </div>
        )}

        {(allPwDetails.length > 1 ||
          (allPwDetails.length === 1 && finishedChoosingPrizes)) && (
          <div className="fullWidth flexCentered">
            <div className="prizeTabs flexCentered flexRow vertical-margin-1em">
              {allPwDetails.map((details, idx) => (
                <div
                  key={`prize-${idx + 1}`}
                  className={extendClassName('prizeTab', [
                    idxOfSelectedPw === idx ? 'selected' : '',
                    !unclaimedPrizeWinStatuses.includes(details.status)
                      ? 'chosen'
                      : '',
                  ])}
                  onClick={() =>
                    (unclaimedPrizeWinStatuses.includes(details.status) ||
                      finishedChoosingPrizes) &&
                    idx === idxOfSelectedPw
                      ? setIdxOfSelectedPw(null)
                      : setIdxOfSelectedPw(idx)
                  }
                >
                  <p className="medium noMargin bold">
                    {allPwDetails.length === 1 ? (
                      'My Treat'
                    ) : (
                      <>
                        {!unclaimedPrizeWinStatuses.includes(
                          details.status,
                        ) && (
                          <span style={{ marginLeft: '-10px' }}>✓</span>
                        )}{' '}
                        Treat {idx + 1}
                      </>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {detailsForSelectedPw && (
          /* Use a key so that react recreates the markup. This prevents the img element images swapping out weirdly */
          <div
            key={detailsForSelectedPw.id}
            className="prizeWinDetails flexCentered fullWidth vertical-margin-1em"
          >
            <p className="medium">
              {/* You matched{' '}
              <strong>
                {describeNumberOfMatchesInPlainEnglish(
                  detailsForSelectedPw.matches
                )}
                !
              </strong>
              <br /> */}
              {selectedPwHasBeenChosen ? (
                <>The following treat has been added to your account</>
              ) : (
                <>Choose any of the treats below</>
              )}
            </p>

            {prizeOptionsForSelectedPw &&
              prizeOptionsForSelectedPw.allAvailablePrizes.length > 0 && (
                <div className="redemptionOptions flexCentered fullWidth vertical-margin-1em">
                  {prizeOptionsForSelectedPw.allAvailablePrizes
                    .filter((prizeOption) =>
                      selectedPwHasBeenChosen
                        ? prizeOption.id === detailsForSelectedPw.chosenPrizeId
                        : true,
                    )
                    .map((prizeOption, idx) => (
                      <PrizeCardWithTitleBelow
                        key={`prize-option-${idxOfSelectedPw}-${idx}`}
                        prize={prizeOption}
                        onClick={() => setSelectedPrizeOption(prizeOption)}
                        customImageOpacity={1}
                        redeemPrizeButton={
                          selectedPwHasBeenChosen ? null : (
                            <RoundedButton
                              onClick={() =>
                                setSelectedPrizeOption(prizeOption)
                              }
                              disabled={waitingForSubmitPrizeChoiceResult}
                              style={{ margin: '2em 0 0', flex: 1 }}
                            >
                              Redeem treat
                            </RoundedButton>
                          )
                        }
                        withFavouriteButton={false}
                        showLockedPrizeBannerIfApplicable={false}
                      />
                    ))}
                </div>
              )}

            {selectedPwHasBeenChosen && !finishedChoosingPrizes && (
              <div
                className="vertical-margin-1em"
                onClick={() =>
                  setIdxOfSelectedPw(findIndexOfNextPendingPrizeWin())
                }
              >
                <p className="medium clickable xbold underline">
                  Choose Treat {findIndexOfNextPendingPrizeWin() + 1}
                </p>
              </div>
            )}

            {!selectedPwHasBeenChosen && (
              <div className="vertical-margin-1em">
                <p className="medium italic">
                  You have {PRIZE_WIN_VALIDITY_IN_DAYS} days to claim this
                  treat. After that, we'll donate your win to veritree in the
                  form of 1 tree 🌳
                </p>
              </div>
            )}
          </div>
        )}

        {finishedChoosingPrizes && (
          <div className="vertical-margin-2em">
            <p className="xlarge xbold">Done!</p>

            <img
              className="enjoy-gif"
              src={`${ASSET_PREFIX}/images/my-wins/enjoy1.gif`}
              alt="Enjoy your prize!"
            />
          </div>
        )}

        {finishedChoosingPrizes && (
          <BackButton onClick={() => router.push('/my-wins')}>
            Back to My Wins
          </BackButton>
        )}
      </PaddedMaxWidthContainer>

      <GreenPageSection additionalClassName="tp-review-section">
        <Subheading>
          How are we <strong>doing?</strong>
        </Subheading>

        <p className="medium">
          We'd love to know what you think — good or bad! Leave us a review on{' '}
          <a
            href="https://uk.trustpilot.com/review/daymade.co.uk"
            target="_blank"
            className="xbold underline"
            rel="noreferrer"
          >
            Trustpilot
          </a>{' '}
          or shoot us an{' '}
          <a href="mailto:hello@daymade.co.uk" className="xbold underline">
            email
          </a>{' '}
          at any time to let us know your thoughts.
        </p>

        <RoundedButton
          className="vertical-margin-1em"
          href="https://uk.trustpilot.com/review/daymade.co.uk"
          linkIsExternal
        >
          Leave a Trustpilot review
        </RoundedButton>
      </GreenPageSection>

      <style jsx>
        {`
          .prizeTabs {
            overflow-x: auto;
            justify-content: flex-start;
            max-width: 100%;
          }

          .prizeTabs::-webkit-scrollbar {
            display: none;
          }

          .prizeTab {
            padding: 0.75em 2.5em;
            margin: 0 0.5em;
            border-radius: 30px;
            background: white;
            border: 2px solid ${colors.primaryBrandGray};
            cursor: pointer;
          }

          .prizeTab.chosen {
            background: ${colors.primaryBrandGray}50;
            border-color: ${colors.primaryBrandGray}10;
            color: white;
          }

          .prizeTab.selected {
            background: ${colors.primaryBrandGray};
            color: white;
          }

          .prizeTab.chosen.selected {
            border-color: ${colors.primaryBrandGray};
          }

          .prizeTab p {
            white-space: nowrap;
          }

          .redemptionOptions {
            width: 100%;
            flex-direction: row;
            align-items: flex-start;
            flex-wrap: wrap;
          }

          :global(.tp-review-section) {
            max-width: 700px;
          }

          .choose-treats-icon {
            width: 16%;
            margin-bottom: 0.75em;
          }

          .enjoy-gif {
            overflow: hidden;
            border-radius: 20px;
            width: 100%;
            max-width: 500px;
          }

          :global(.popupWithDimmedBackground p) {
            font-size: 0.9rem;
          }

          @media (min-width: 700px) {
            :global(.popupWithDimmedBackground p) {
              font-size: 1.05rem;
            }
          }
        `}
      </style>
    </MainLayout>
  );
});

export default ChooseTreats;
